import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Icon from '../../../components/icon/Icon';
import Checkbox from '../../../components/checkBox/CheckBox';
import Input from '../../../components/input/Input';
import MultiSelectDropdown from '../../../components/multiselectdropdown/MultiSelectDropdown';
import { signersData } from '../../../types/interfaces';
import { SIGNERS_TYPES } from '../../../utils/Constants';
import { downloadSignerDocument } from '../../../services/ApplicationService';
import { getMimeType } from '../../../utils/Utils';
import Label from '../../../components/texts/label/Label';
import styled from 'styled-components';
import theme from '../../../styles/theme';
import { useClientTheme } from '../../../context/ThemeContext';

const FileCardContainer = styled(Card)`
  opacity: 0.5;
`;

const FileLabelStyled = styled(Label)`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
`;


const ReadOnlyMultiSelectWrapper = styled.div`
  width: 100%;
  
  > div > div {
    background-color: #ffffff !important;
  }
  
  /* Ocultar el icono de chevron (flecha) */
  > div > div > div:last-of-type {
    display: none !important;
  }
`;

const ReadOnlyInputWrapper = styled.div`
  width: 100%;
  
  > div {
    opacity: 0.85;
    background-color: #f5f5f5 !important;
    
    &:hover {
      box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors?.neutral?.border?.regular || '#e0e0e0'} !important;
      cursor: default !important;
    }
  }
  
  input,
  input[type="text"],
  input[type="file"] {
    font-size: 12px !important;
    color: ${theme.colors.neutral.text.weak} !important;
    opacity: 1 !important;
    background-color: transparent !important;
    cursor: default !important;
    
    &:hover {
      cursor: default !important;
    }
  }
  
  input[type="file"] {
    cursor: default !important;
    pointer-events: none;
    
    &:hover {
      cursor: default !important;
    }
  }
  
  div div input {
    font-size: 12px !important;
    color: ${theme.colors.neutral.text.weak} !important;
    opacity: 1 !important;
    background-color: transparent !important;
    cursor: default !important;
  }
`;


interface SignersReadOnlyProps {
  signers: signersData[];
  cuit: string | undefined;
  applicationId: string | undefined;
}

const SignersReadOnly: React.FC<SignersReadOnlyProps> = ({
  signers,
  cuit,
  applicationId,
}) => {
  const { clientTheme } = useClientTheme();
  
  const handleViewFile = (filename: string) => {
    if (!filename || !applicationId || !cuit) return;
    
    downloadSignerDocument(applicationId, cuit, filename).then((response) => {
      if (response.success) {
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);

        if (
          mimeType.startsWith('application/pdf') ||
          mimeType.startsWith('image/')
        ) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    });
  };


  if (!signers || signers.length === 0) {
    return (
      <Card>
        <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            Firmantes
          </H>
        </Card>
        <Card margin={{ top: '4' }}>
          <P type='2' variant='neutral' modifier='weak' withMargin={false}>
            No hay firmantes cargados
          </P>
        </Card>
      </Card>
    );
  }

  return (
    <Card>
      <Card
        variant={clientTheme ? 'primary' : 'success'}
        modifier='weak'
        spacing={'2'}
        display='flex'
        justifyContent='space-between'
      >
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Firmantes
        </H>
        <Checkbox
          id='instrumentation-readonly-checkbox'
          checked={true}
          disabled={true}
        >
          La información se ajusta a los documentos
        </Checkbox>
      </Card>
      <Card
        margin={{ top: '4' }}
        display='flex'
        gap='2px'
        flexWrap='wrap'
        flexDirection={signers.length > 1 ? 'row' : 'column'}
      >
        {signers.map((signer, index) => (
          <Card
            key={index}
            display='flex'
            gap='12px'
            width={signers.length > 1 ? 'calc(33.33% - 2px)' : '100%'}
            margin={{ bottom: '3' }}
            flexDirection='column'
            style={{ flexShrink: 0 }}
          >
            <H
              type='3'
              variant='neutral'
              weight='bold'
              modifier='regular'
              withMargin={false}
            >
              Firmante {index + 1}
            </H>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                CUIL
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.cuit || ''}
                    placeholder='CUIL'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Nombre
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.name || ''}
                    placeholder='Nombre'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Mail
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.email || ''}
                    placeholder='mail@mail.com'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Localidad
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.locality || ''}
                    placeholder='Buscar localidad...'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Domicilio
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.address || ''}
                    placeholder='Calle y número'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Caracter legal
              </P>
              <Card width='400px'>
                <ReadOnlyInputWrapper>
                  <Input
                    value={signer.role || ''}
                    placeholder='Caracter legal'
                    type='text'
                    disabled={true}
                  />
                </ReadOnlyInputWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Tipo de firma
              </P>
              <Card width='400px'>
                <ReadOnlyMultiSelectWrapper>
                  <MultiSelectDropdown
                    options={SIGNERS_TYPES.map((type) => ({
                      label: type.label,
                      value: type.id,
                    }))}
                    selectedValues={signer.signer_type_code || []}
                    onChange={() => {}}
                    placeholder='Elegir'
                    disabled={true}
                    singleSelect={false}
                  />
                </ReadOnlyMultiSelectWrapper>
              </Card>
            </Card>

            <Card display='flex' gap='0px' flexDirection='column'>
              <P type='2' variant='neutral' weight='bold' modifier='strong' withMargin={false}>
                Archivo de bienes
              </P>
              <Card width='400px' style={{ overflow: 'hidden' }}>
                {signer.asset_file_path ? (
                  <FileCardContainer
                    variant='neutral'
                    modifier='inverted'
                    height='48px'
                    spacing={{ left: '2', right: '2' }}
                    borderRadiusAmount='3'
                    border
                    display='flex'
                    gap='8px'
                    alignItems='center'
                    style={{ boxSizing: 'border-box', width: '100%', maxWidth: '100%' }}
                  >
                    <FileLabelStyled
                      variant='neutral'
                      modifier='strong'
                      type='2'
                    >
                      {signer.asset_file_path}
                    </FileLabelStyled>
                    <Icon
                      name='view_outline'
                      variant='neutral'
                      height={24}
                      cursor='pointer'
                      onClick={() => handleViewFile(signer.asset_file_path)}
                    />
                  </FileCardContainer>
                ) : (
                  <Card>
                    <ReadOnlyInputWrapper>
                      <Input
                        type='file'
                        size='md'
                        disabled={true}
                      />
                    </ReadOnlyInputWrapper>
                  </Card>
                )}
              </Card>
            </Card>
          </Card>
        ))}
      </Card>
    </Card>
  );
};

export default SignersReadOnly;

